import { insert, isNil } from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import videojs, { VideoJsPlayerOptions } from 'video.js';

import { AD_TAG_URL, adTagUrlMap } from '@src/demo/constants/ads';
import { DefaultTheme, Theme, ThemeClass } from '@src/types/theme';
import { DefaultTrackingScheme, TrackingScheme } from '@src/types/tracking';
import { getDebugFlags } from '@src/utils/debug';
import { getBrowserVersion, getIosVersion } from '@src/utils/device';
import logger from '@src/utils/logger';
import {
  Child as OptionChild,
  getFirstMatchedChild,
  getMatchChild,
} from '@src/utils/vjs';

export const Player = videojs.getComponent('Player');
export const ControlBar = videojs.getComponent('ControlBar');

const insertUserInactiveOverlay = (children: OptionChild[]) => {
  const { index } =
    getFirstMatchedChild(['posterImage', 'mediaLoader'], children) ?? {};

  return insert(isNil(index) ? 0 : index + 1, 'UserInactiveOverlay', children);
};

const insertFloatingInfo = (
  children: OptionChild[],
  playerOptions: VideoJsPlayerOptions
) => {
  const { index } =
    getFirstMatchedChild(
      ['textTrackDisplay', 'posterImage', 'mediaLoader'],
      children
    ) ?? {};

  return insert(
    isNil(index) ? 0 : index + 1,
    {
      name: 'FloatingInfo',
      title: playerOptions.title,
      description: playerOptions.description,
    },
    children
  );
};

const insertFloatingDurationDisplay = (children: OptionChild[]) => {
  const { index } = getFirstMatchedChild(['bigPlayButton'], children) ?? {};

  return insert(
    isNil(index) ? 0 : index + 1,
    'FloatingDurationDisplay',
    children
  );
};

const insertFloatingCloseButton = (children: OptionChild[]) => {
  const { index } = getFirstMatchedChild(['controlBar'], children) ?? {};

  return insert(isNil(index) ? 0 : index + 1, 'FloatingCloseButton', children);
};

const insertToastDisplay = (children: OptionChild[]) => {
  const { index } = getFirstMatchedChild(['controlBar'], children) ?? {};

  return insert(isNil(index) ? 0 : index + 1, 'ToastDisplay', children);
};

const replacePosterImage = (children: OptionChild[]) => {
  const clonedChildren = [...children];

  const replaceIndex = children.findIndex((child) =>
    getMatchChild(child, 'posterImage')
  );

  if (replaceIndex !== -1) {
    clonedChildren[replaceIndex] = 'CustomPosterImage';
  }

  return clonedChildren;
};

export const injectErrorLogger = (options: VideoJsPlayerOptions) => {
  const originalOnError = options.onError;
  const originalOnAdError = options.onAdError;

  options.onError =
    originalOnError === logger.error
      ? originalOnError
      : (...args) => {
          logger.error(...args);
          originalOnError?.(...args);
        };

  options.onAdError =
    originalOnAdError === logger.error
      ? originalOnAdError
      : (...args) => {
          logger.error(...args);
          originalOnAdError?.(...args);
        };

  return options;
};

const getControlBarOptions = (options: VideoJsPlayerOptions) => {
  return {
    pictureInPictureToggle: false,
    ...(options.controlBar ?? {}),
  };
};

const updateAdOptions = (options: VideoJsPlayerOptions) => {
  const debugFlags = getDebugFlags();

  if (debugFlags?.includes('vdoblockad')) {
    options.blockAd = true;
  }

  if (debugFlags?.includes('testpremidpostroll')) {
    options.adTagUrl = adTagUrlMap.get(
      AD_TAG_URL.VMAP_PRE_MID_AND_POST_ROLLS_SINGLE_ADS
    );
  }

  if (debugFlags?.includes('testprerollskip')) {
    options.adTagUrl = adTagUrlMap.get(AD_TAG_URL.SINGLE_SKIPPABLE_INLINE);
  }

  if (debugFlags?.includes('testprerollvmap')) {
    options.adTagUrl = adTagUrlMap.get(AD_TAG_URL.VMAP_PRE_ROLL);
  }

  if (debugFlags?.includes('testpreroll')) {
    options.adTagUrl = adTagUrlMap.get(AD_TAG_URL.SINGLE_INLINE_LINEAR);
  }

  return options;
};

const updateOptionsOfIframeFullscreen = (options: VideoJsPlayerOptions) => {
  const debugFlags = getDebugFlags();

  const isDebugShowFullscreenToggle =
    !!debugFlags?.includes('vdoshowfullscreen');
  const isDebugHideFullscreenToggle =
    !!debugFlags?.includes('vdohidefullscreen');

  if (isDebugShowFullscreenToggle || isDebugHideFullscreenToggle) {
    if (isDebugShowFullscreenToggle) {
      options.hideFullscreenToggle = false;
    }

    if (isDebugHideFullscreenToggle) {
      options.hideFullscreenToggle = true;
    }

    return options;
  }

  const browserVersion = getBrowserVersion();

  const isSafariOnMacOrIpad =
    videojs.browser.IS_SAFARI &&
    (!videojs.browser.IS_IOS || videojs.browser.IS_IPAD);

  const isInvalidSafariToEnableFullscreen =
    isSafariOnMacOrIpad &&
    browserVersion &&
    Number.parseFloat(browserVersion) < 16.4;

  const isChromeOnIpad = videojs.browser.IS_CHROME && videojs.browser.IS_IPAD;

  const iosVersion = getIosVersion();

  const isInvalidChromeToEnableFullscreen =
    isChromeOnIpad && iosVersion && iosVersion >= 16.4;

  // some browsers has UI stacking issue in iframe fullscreen mode
  // base on testing result from QA — https://hk01-digital.atlassian.net/browse/GROWTH-3707?focusedCommentId=115879
  if (
    options.inIframe &&
    isNil(options.hideFullscreenToggle) &&
    (isInvalidSafariToEnableFullscreen || isInvalidChromeToEnableFullscreen)
  ) {
    options.hideFullscreenToggle = 'inlineonly';
  }

  return options;
};

videojs.hook(
  'beforesetup',
  (videoEl: HTMLVideoElement, options: VideoJsPlayerOptions) => {
    if (options.theme !== false) {
      // theme
      const userTheme = options.theme ?? DefaultTheme;
      const theme = Object.values(Theme).includes(userTheme)
        ? userTheme
        : DefaultTheme;
      const themeClassName = ThemeClass[theme];
      const containerEl = videoEl.closest('.video-js');
      containerEl?.classList.add(themeClassName);

      options.theme = theme;

      // tracking scheme
      const userTrackingScheme =
        (options.trackingScheme as TrackingScheme) ?? DefaultTrackingScheme;
      const trackingScheme = Object.values(TrackingScheme).includes(
        userTrackingScheme
      )
        ? userTrackingScheme
        : DefaultTrackingScheme;

      options.trackingScheme = trackingScheme;

      options.sessionId = options.sessionId || uuidv4();

      options = injectErrorLogger(options);

      if (theme === Theme.V5DEFAULT) {
        options.controlBar = getControlBarOptions(options);

        options.children =
          options.children ?? (Player.prototype.options_.children || []);

        options.children = insertFloatingInfo(options.children, options);

        options.children = insertUserInactiveOverlay(options.children);

        options.children = insertFloatingDurationDisplay(options.children);

        options.children = insertFloatingCloseButton(options.children);

        options.children = insertToastDisplay(options.children);

        options.children = replacePosterImage(options.children);

        options.children = options.children.filter(
          (child) => !getMatchChild(child, 'textTrackSettings')
        ); // disable text track settings dialog

        options = updateAdOptions(options);

        options = updateOptionsOfIframeFullscreen(options);
      }
    }

    return options;
  }
);
